import React, { useEffect, useState } from 'react';
import { Briefcase, Home, Pencil, Trash2 } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { useAdminRole } from '../hooks/useAdminRole.ts';
import {
  OfertaLaboral,
  createOfertaLaboral,
  deleteOfertaLaboral,
  getOfertasLaborales,
  updateOfertaLaboral,
} from '../services/bolsaTrabajoService.ts';

const initial = {
  titulo: '', empresa: '', ubicacion: 'San Carlos de Bolívar', modalidad: 'Presencial', tipo: 'Full-time', categorias: [], descripcion: '', contacto: '', activo: true,
} as Omit<OfertaLaboral, 'oferta_id' | 'publicado_en'>;

export default function BolsaTrabajoAdmin() {
  const { isAdmin } = useAdminRole();
  const [items, setItems] = useState<OfertaLaboral[]>([]);
  const [form, setForm] = useState(initial);
  const [categoriasInput, setCategoriasInput] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = async () => setItems(await getOfertasLaborales(false));
  useEffect(() => { load(); }, []);
  if (!isAdmin) return <Navigate to="/inicio" replace />;

  const guardar = async () => {
    if (!form.titulo.trim() || !form.empresa.trim() || !form.descripcion.trim() || !form.contacto.trim()) return;
    const categorias = categoriasInput
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);

    const payload = { ...form, categorias };

    if (editingId) await updateOfertaLaboral(editingId, payload);
    else await createOfertaLaboral(payload);
    setForm(initial); setCategoriasInput(''); setEditingId(null); await load();
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4">
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center"><Link to="/inicio" className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800"><Home className="w-4 h-4 mr-2" />Inicio</Link></li>
          <li><div className="flex items-center"><Briefcase className="w-4 h-4 text-gray-400" /><span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">Admin Bolsa de Trabajo</span></div></li>
        </ol>
      </nav>

      <div className="rounded-xl bg-white p-4 shadow space-y-3">
        <h1 className="text-2xl font-bold">ABM de búsquedas laborales</h1>
        <div className="grid gap-2 md:grid-cols-2">
          <input className="rounded border p-2" placeholder="Título" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
          <input className="rounded border p-2" placeholder="Empresa" value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} />
          <input className="rounded border p-2" placeholder="Ubicación" value={form.ubicacion || ''} onChange={(e) => setForm({ ...form, ubicacion: e.target.value })} />
          <input className="rounded border p-2" placeholder="Contacto (mail/tel)" value={form.contacto} onChange={(e) => setForm({ ...form, contacto: e.target.value })} />
          <input
            className="rounded border p-2 md:col-span-2"
            placeholder="Categorías (separadas por coma). Ej: Servicios, Electricista, Casa empleada"
            value={categoriasInput}
            onChange={(e) => setCategoriasInput(e.target.value)}
          />
          <select className="rounded border p-2" value={form.modalidad} onChange={(e) => setForm({ ...form, modalidad: e.target.value as any })}><option>Presencial</option><option>Híbrido</option><option>Remoto</option></select>
          <select className="rounded border p-2" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as any })}><option>Full-time</option><option>Part-time</option><option>Contrato</option><option>Pasantía</option></select>
          <textarea className="rounded border p-2 md:col-span-2" rows={3} placeholder="Descripción" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} /> Activa</label>
        </div>
        <div className="flex gap-2">
          <button className="rounded bg-blue-600 px-3 py-2 text-white" onClick={guardar}>{editingId ? 'Guardar cambios' : 'Publicar oferta'}</button>
          {editingId && <button className="rounded border px-3 py-2" onClick={() => { setEditingId(null); setForm(initial); setCategoriasInput(''); }}>Cancelar</button>}
        </div>
      </div>

      <div className="rounded-xl bg-white p-4 shadow overflow-x-auto">
        <table className="min-w-full text-sm"><thead><tr className="border-b text-left text-slate-500"><th className="py-2">Oferta</th><th className="py-2">Empresa</th><th className="py-2">Estado</th><th className="py-2">Acciones</th></tr></thead>
          <tbody>{items.map((o) => <tr key={o.oferta_id} className="border-b"><td className="py-2">{o.titulo}</td><td className="py-2">{o.empresa}</td><td className="py-2">{o.activo ? 'Activa' : 'Inactiva'}</td><td className="py-2"><div className="flex gap-2"><button className="rounded border px-2 py-1" onClick={() => { setEditingId(o.oferta_id); setForm({ ...o }); setCategoriasInput((o.categorias || []).join(', ')); }}><Pencil className="h-4 w-4" /></button><button className="rounded border px-2 py-1 text-rose-600" onClick={async () => { await deleteOfertaLaboral(o.oferta_id); await load(); }}><Trash2 className="h-4 w-4" /></button></div></td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}
